import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { CustomEncoder } from '../encoder/custom-encoder';
import { ENVIRONMENT } from '../settings/custom.tokens';
import { Environment, LightWeightUser, UserAccount } from '@iot-platform/models/common';
import { catchError, Observable, of } from 'rxjs';
import { map, retry, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CachedUsersService {
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly http: HttpClient = inject(HttpClient);
  cachedUsers: WritableSignal<LightWeightUser[]> = signal([]);
  public readonly systemUuid = '00000000-0000-0000-0000-000000000000';

  loadUsers(page = 0): void {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    params = params.set('page', page);
    this.http
      .get<{ content: LightWeightUser[]; page: { limit: number; hasMore: boolean; curPage: number; maxPage: number } }>(
        `${this.environment.api.url}${this.environment.api.endpoints.usersLightWeight}`,
        { params }
      )
      .pipe(
        retry(2),
        tap((response) => {
          this.cachedUsers.update((cachedUsers) => [...cachedUsers, ...response.content]);

          if (response.page.hasMore && response.page.curPage !== response.page.maxPage - 1) {
            this.loadUsers(page + 1);
          }
        })
      )
      .subscribe();
  }

  public getUserById(userId: string): Observable<LightWeightUser | undefined> {
    return of(this.cachedUsers()).pipe(
      switchMap((users) => {
        const user: LightWeightUser | undefined = users.find((u) => u.id === userId);

        if (user) {
          return of(user);
        } else {
          return this.http.get<UserAccount>(`${this.environment.api.url}${this.environment.api.endpoints.users}/${userId}`).pipe(
            map((u) => ({ id: u.id, firstname: u.firstname, lastname: u.lastname, status: u.status })),
            catchError((error) => of(error)),
            tap((u: LightWeightUser) => {
              let result = u;
              if (result instanceof HttpErrorResponse) {
                result = { id: userId, status: 'error', firstname: '', lastname: '' };
              }
              return this.cachedUsers.update((cachedUsers) => [...cachedUsers, result]);
            })
          );
        }
      })
    );
  }
}
